import React from 'react';
import { cx, CARD_SURFACE, ROW_SURFACE } from './styles';

/**
 * Loading placeholders shaped like the content that is about to arrive, in
 * place of v1's "spinner + Loading…" blocks. The loading *condition* never
 * changes — same `isLoading` flag, same branch, different pixels.
 *
 * Shimmer is `animate-pulse` on `bg-white/[0.04]`; newui.css disables it under
 * `prefers-reduced-motion: reduce`.
 */

export interface SkeletonProps {
  className?: string;
  /** Rounded pill instead of the default rounded-md. */
  pill?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = 'h-4 w-full', pill }) => (
  <div
    aria-hidden="true"
    className={cx('animate-pulse bg-white/[0.04]', pill ? 'rounded-full' : 'rounded-md', className)}
  />
);

/** One list row: leading square, two stacked lines, trailing value. */
export const SkeletonRow: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cx(ROW_SURFACE, 'flex items-center gap-4 p-4', className)}>
    <Skeleton className="h-10 w-10 flex-shrink-0 rounded-lg" />
    <div className="min-w-0 flex-1 space-y-2">
      <Skeleton className="h-3.5 w-1/2" />
      <Skeleton className="h-3 w-1/3" />
    </div>
    <Skeleton className="h-3.5 w-16 flex-shrink-0" />
  </div>
);

/** One card: title, two body lines, a chip strip. */
export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cx(CARD_SURFACE, 'space-y-3 p-5', className)}>
    <Skeleton className="h-4 w-2/5" />
    <Skeleton className="h-3 w-3/5" />
    <div className="flex gap-2 pt-1">
      <Skeleton className="h-5 w-16" pill />
      <Skeleton className="h-5 w-20" pill />
    </div>
  </div>
);

/** One stat tile: label above a large number. */
export const SkeletonTile: React.FC<{ className?: string }> = ({ className }) => (
  <div className={cx('rounded-xl border border-white/10 bg-white/[0.02] p-4', className)}>
    <Skeleton className="mb-2 h-2.5 w-12" />
    <Skeleton className="h-7 w-16" />
  </div>
);

/** `count` rows / cards / tiles, correctly keyed. */
export const SkeletonList: React.FC<{
  count?: number;
  as?: 'row' | 'card' | 'tile';
  className?: string;
}> = ({ count = 3, as = 'row', className }) => {
  const Item = as === 'card' ? SkeletonCard : as === 'tile' ? SkeletonTile : SkeletonRow;
  return (
    <div className={cx(as === 'tile' ? 'grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5' : 'space-y-3', className)}>
      {Array.from({ length: count }, (_, i) => (
        <Item key={i} />
      ))}
    </div>
  );
};
